import * as React from "react";
import { Alert, Button, Card, Divider } from "antd";
import { CardTitle } from "../../../components/antd/card/CardTitle";
import { CardContent } from "../../../components/antd/card/CardContent";
import { useDebounce } from "../../../components/table/useDebounce";
import { homeLink, linksOf, parentLink, request, resourceLink } from "../../../services/cpln";
import { parseLink } from "../../../utils/linkParser/linkParser";
import { Policy } from "../../../schema/types/policy/entity";
import { QueryResult } from "../../../schema/types/query/query";
import { Secret } from "../../../schema/types/secret/entity";
import { store } from "../../../store";
import { formSetValue, formTriggerValidation, useCpFormValue } from "../../gvc/testNewForm/utils";
import { CpFormItem } from "../../gvc/testNewForm/CpFormItem";
import { CpLabel } from "../../gvc/testNewForm/CpLabel";
import { CpDescription } from "../../gvc/testNewForm/CpDescription";
import { get } from "lodash";
import { Loader } from "react-feather";
import { CpInput } from "../../gvc/testNewForm/CpInput";
import objectHash from "object-hash";
import { CpErrorWarningArrayIcon } from "../../gvc/testNewForm/CpErrorWarningArrayIcon";
import { useCpFormContext } from "../../../components/form/useCpFormContext";
import { CpKindSelect } from "../../gvc/testNewForm/temp/CpKindSelect";
import { CpTooltip } from "../../gvc/testNewForm/CpTooltip";
import { CpLabelTooltip } from "../../gvc/testNewForm/CpLabelTooltip";
import { CpLabelDirtyIndicator } from "../../gvc/testNewForm/CpLabelDirtyIndicator";
import { Only } from "./only";
import { useUsedSecretLinks } from "./utils/utils";
import { useGetIdentityQuery } from "../../../store/api/identityApi";
import { InternalLink } from "../../../newcomponents/table/components/InternalLink";
import { useAppSelector } from "../../../store/hooks";
import { selectOrg } from "../../../store/contextSlice";

interface Props {}
export const WorkloadIdentityLink: React.FC<Props> = ({}) => {
  const { id, submit } = useCpFormContext();
  const usedSecretLinks = useUsedSecretLinks();
  const org = useAppSelector(selectOrg);

  const usedSecretsHash = React.useMemo(() => {
    return objectHash(usedSecretLinks);
  }, [usedSecretLinks]);

  // Debounced validation trigger when secrets change
  const [debouncedSecretsHash, setDebouncedSecretsHash] = useDebounce(usedSecretsHash, 500);
  React.useEffect(() => {
    setDebouncedSecretsHash(usedSecretsHash);
  }, [usedSecretsHash]);

  const isFirstSecretsRender = React.useRef(true);
  React.useEffect(() => {
    // Skip first render to avoid unnecessary validation on mount
    if (isFirstSecretsRender.current) {
      isFirstSecretsRender.current = false;
      return;
    }
    formTriggerValidation(id);
  }, [debouncedSecretsHash]);

  const identityIsRequired = React.useMemo(() => {
    return usedSecretLinks.length > 0;
  }, [usedSecretLinks]);

  const isCreate = useCpFormValue(`meta.isCreate`);
  const gvc = useCpFormValue(`gvc`);
  const name = useCpFormValue(`name`);
  const identity = useCpFormValue(`identity.value`);

  const [debouncedIdentity, setDebouncedIdentity] = useDebounce(identity, 300);
  React.useEffect(() => {
    setDebouncedIdentity(identity);
  }, [identity]);

  const isValidating = useCpFormValue(`identity.isValidating`);
  const isValid = useCpFormValue(`identity.isValid`);
  const invalidError = useCpFormValue(`identity.invalidError`);
  const policyName = useCpFormValue(`identity.policyName`);
  const identityName = useCpFormValue(`identity.identityName`);
  const isFixing = useCpFormValue(`identity.isFixing`);
  const fixError = useCpFormValue(`identity.fixError`);

  const { data: identityData, isError: identityAccessError } = useGetIdentityQuery(identity?.link, {
    skip: !identity?.link,
  });

  const identityUrl =
    identity?.value && gvc?.value ? `/console/org/${org}/gvc/${gvc.value}/identity/${identity.value}` : null;

  React.useEffect(() => {
    if (!name || !identityIsRequired) {
      return;
    }

    const defaultName = identity?.value || name;
    formSetValue(id, `identity.identityName`, defaultName);
    formSetValue(id, `identity.policyName`, defaultName);
  }, [name, identityIsRequired]);

  // Clear identity when GVC changes
  const prevGvcRef = React.useRef<string>(gvc.value);

  React.useEffect(() => {
    if (prevGvcRef.current === gvc.value) {
      return;
    }

    // GVC changed - check if identity is from old GVC
    if (identity?.link) {
      const parsedIdentity = parseLink(identity.link, { useInputCtx: true });

      if (parsedIdentity.gvc && parsedIdentity.gvc !== gvc.value) {
        formSetValue(id, `identity.value`, { value: "", link: "", item: null });
        formSetValue(id, `identity.isValid`, false);
        formSetValue(id, `identity.isValidating`, false);
        formSetValue(id, `identity.invalidError`, "");
      }
    }

    prevGvcRef.current = gvc.value;
  }, [gvc.value, identity?.link]);

  const [lastCheckedIdentity, setLastCheckedIdentity] = React.useState("");
  const [lastCheckedSecretsHash, setLastCheckedSecretsHash] = React.useState("");

  // Request ID ref to prevent race conditions in async validation
  const validationRequestIdRef = React.useRef(0);

  React.useEffect(() => {
    validateIdentity();
  }, [gvc.value, usedSecretsHash, identityIsRequired, debouncedIdentity]);

  // Force initial validation on edit mode
  const initialValidationDoneRef = React.useRef(false);

  React.useEffect(() => {
    if (initialValidationDoneRef.current || isCreate) return;

    if (identity?.link && gvc.value) {
      initialValidationDoneRef.current = true;
      // Reset last checked values to force validation to run
      setLastCheckedIdentity("");
      setLastCheckedSecretsHash("");
    }
  }, [identity?.link, gvc.value, isCreate]);

  async function validateIdentity() {
    try {
      if (!identity || !identityIsRequired || !gvc.value) {
        formSetValue(id, `identity.isValidating`, false);
        return;
      }

      const form = store.getState().form.instances[id];
      const values = form.values;

      const identityLink = get(values, `identity.value.link`);

      if (lastCheckedIdentity === identityLink && lastCheckedSecretsHash === usedSecretsHash) {
        return;
      }

      // Increment request ID to track this specific validation request
      validationRequestIdRef.current = validationRequestIdRef.current + 1;
      const currentRequestId = validationRequestIdRef.current;

      formSetValue(id, `identity.isValidating`, true);
      formSetValue(id, `identity.isValid`, false);

      // Verify identity exists before checking permissions
      try {
        await request({ method: "get", url: identityLink });
      } catch (e: any) {
        if (currentRequestId !== validationRequestIdRef.current) return;

        if (e?.response?.status === 404) {
          formSetValue(id, `identity.isValidating`, false);
          formSetValue(id, `identity.isValid`, false);
          formSetValue(id, `identity.invalidError`, "Identity not found - it may have been deleted");
          setLastCheckedIdentity(identityLink);
          setLastCheckedSecretsHash(usedSecretsHash);
          return;
        }
        // Continue for other errors - user may lack GET permission but can still use identity
      }

      // Check if this request is stale after existence check
      if (currentRequestId !== validationRequestIdRef.current) return;

      // fetch all policies
      let queryLink = homeLink("policy") + "/-query";
      let policies: Policy[] = [];
      const queryBody = {
        kind: "policy",
        spec: {
          match: "all",
          terms: [
            {
              op: "=",
              property: "targetKind",
              value: "secret",
            },
          ],
        },
      };

      let { data }: any = await request({ method: "post", url: queryLink, body: queryBody });

      // Check if this request is stale (newer request started)
      if (currentRequestId !== validationRequestIdRef.current) return;

      policies = policies.concat(data.items);

      let nextLink = data.links.find((link: any) => link.rel === "next")?.href;
      while (nextLink) {
        let { data } = await request({ url: nextLink });

        // Check if this request is stale
        if (currentRequestId !== validationRequestIdRef.current) return;

        policies = policies.concat(data.items);
        nextLink = data.links.find((link: any) => link.rel === "next")?.href;
      }

      // filter reveal, edit, manage permissions for this identity
      policies = policies.filter(
        (p) =>
          p.bindings &&
          Array.isArray(p.bindings) &&
          p.bindings.some(
            (b) =>
              b.permissions &&
              Array.isArray(b.permissions) &&
              (b.permissions.includes("reveal") ||
                b.permissions.includes("edit") ||
                b.permissions.includes("manage")) &&
              b.principalLinks &&
              Array.isArray(b.principalLinks) &&
              b.principalLinks.includes(identityLink),
          ),
      );

      // checks if target item is all for any of the policies, then we have validated the requirements
      if (policies.some((p) => p.target === "all")) {
        // Check if this request is stale before updating state
        if (currentRequestId !== validationRequestIdRef.current) return;

        setLastCheckedIdentity(identityLink);
        formSetValue(id, `identity.isValidating`, false);
        formSetValue(id, `identity.isValid`, true);
        return;
      }

      // if policy has a query, execute the query and append to policy targetLinks
      queryLink = parentLink("secret") + "/-query";
      for (let p of policies) {
        if (!p.targetQuery) {
          continue;
        }
        if (!p.targetLinks) {
          p.targetLinks = [];
        }

        const { data: queryResult } = await request<QueryResult<Secret>>({
          method: "post",
          url: queryLink,
          body: p.targetQuery,
        });

        // Check if this request is stale
        if (currentRequestId !== validationRequestIdRef.current) return;

        for (const item of queryResult.items || []) {
          const link = linksOf(item).self!;
          if (!p.targetLinks!.includes(link)) {
            p.targetLinks!.push(link);
          }
        }
      }

      const coveredSecrets: string[] = [];
      for (let p of policies) {
        for (let secretLink of usedSecretLinks) {
          if (coveredSecrets.includes(secretLink)) {
            continue;
          }
          const secretName = parseLink(secretLink, { useInputCtx: true }).name;
          for (const targetLink of p.targetLinks!) {
            const targetSecretName = parseLink(targetLink, { useInputCtx: true }).name;
            if (targetSecretName === secretName) {
              coveredSecrets.push(secretLink);
              break;
            }
          }
        }
      }

      // Check if this request is stale before final state updates
      if (currentRequestId !== validationRequestIdRef.current) return;

      let isValid = usedSecretLinks.length === coveredSecrets.length;
      setLastCheckedIdentity(identityLink);
      setLastCheckedSecretsHash(usedSecretsHash);
      formSetValue(id, `identity.isValidating`, false);
      formSetValue(id, `identity.isValid`, isValid);
      let unconveredSecrets = usedSecretLinks.filter((s) => !coveredSecrets.includes(s));
      formSetValue(id, `identity.invalidError`, unconveredSecrets.join(", "));
    } catch (e) {
      formSetValue(id, `identity.isValidating`, false);
      formSetValue(id, `identity.isValid`, false);
      let errorMessage = e?.response?.data?.message;
      if (!errorMessage) errorMessage = e.message;
      formSetValue(id, `identity.invalidError`, errorMessage);
    }
  }

  async function handleFix() {
    try {
      formSetValue(id, `identity.isFixing`, true);
      formSetValue(id, `identity.fixError`, "");

      const form = store.getState().form.instances[id];
      const values = form.values;

      const identityName = get(values, `identity.identityName`);
      const policyName = get(values, `identity.policyName`);

      const identityBody = {
        name: identityName,
        kind: "identity",
        description: `Auto-managed identity for the workload "${name}"`,
      };
      const policyBody = {
        name: policyName,
        kind: "policy",
        description: `Auto-created policy for the workload "${name}"`,
        targetKind: "secret",
        targetLinks: usedSecretLinks,
        bindings: [{ permissions: ["reveal"], principalLinks: [`//gvc/${gvc.value}/identity/${identityName}`] }],
      };

      const context = { org: store.getState().context.org, gvc: gvc.value };

      // Create identity
      await request({ url: parentLink("identity", context), body: identityBody, method: "put" });
      // Create policy
      await request({ url: homeLink("policy", context), body: policyBody, method: "put" });

      formSetValue(id, `identity.isFixing`, false);
      formSetValue(id, `identity.fixError`, "");
      formSetValue(id, `identity.value`, {
        link: resourceLink("identity", identityName, context),
        name: identityName,
        value: identityName,
      });

      setLastCheckedIdentity("");
      setLastCheckedSecretsHash("");

      if (!isCreate) {
        await submit();
      }
    } catch (e) {
      formSetValue(id, `identity.isFixing`, false);
      if (e.response?.status === 403) {
        formSetValue(id, `identity.fixError`, "You do not have enough permissions");
        return;
      }
      if (e.response?.status !== 409) {
        let errorMessage = e?.response?.data?.message;
        if (!errorMessage) errorMessage = e.message;
        formSetValue(id, `identity.fixError`, errorMessage);
      }
    }
  }

  return (
    <div className="cpln-form-section cpln-ant-card-container">
      <Card>
        <CardTitle>
          <div className="flex items-center gap-2">
            <span>Workload Identity</span>
            <CpErrorWarningArrayIcon names={[`identity`]} />
          </div>
        </CardTitle>
        <CardContent>
          <CpFormItem>
            <CpLabelTooltip>
              <CpLabel name={`identity.value`}>
                Identity
                <Only name={`meta.isCreate`} checkFn={(v) => v === false}>
                  <CpLabelDirtyIndicator name={`identity.value`} />
                </Only>
              </CpLabel>
              <CpTooltip
                tooltipProps={{
                  title: (
                    <div>
                      A workload needing to consume cloud resources from one or more cloud providers (e.g., AWS, Azure,
                      and GCP) without storing credentials or needing to communicate to endpoints within a private
                      network - must be assigned an identity.
                    </div>
                  ),
                }}
              />
            </CpLabelTooltip>
            <div className="flex items-center gap-2">
              <CpKindSelect
                kind="identity"
                name={`identity.value`}
                optionValue="name"
                disabled={!name || !gvc.value}
                queries={[{ rel: "gvc", value: gvc.value }]}
                style={{ width: 450 }}
              />
              {identity?.value && identityData && !identityAccessError && identityUrl && (
                <InternalLink label="View Identity" to={identityUrl} />
              )}
            </div>
          </CpFormItem>
          {!name ? (
            <Alert
              showIcon
              type={`warning`}
              className="my-2 w-fit"
              message={`You need to type workload name from the first section to configure identity`}
            />
          ) : !gvc.value ? (
            <Alert
              showIcon
              type={`warning`}
              className="my-2 w-fit"
              message={`You need to select a GVC from the first section to configure identity`}
            />
          ) : null}
          {identityIsRequired ? (
            <div className="mt-1">
              {!identity?.value ? (
                <Alert
                  type={`error`}
                  showIcon
                  className="my-2 w-fit"
                  message={`Identity is required`}
                  description={
                    <div>
                      <div className="mb-1">Workload cannot reveal these secrets without an identity: </div>
                      <div className="text-sm">
                        {usedSecretLinks.map((secretLink) => (
                          <div key={secretLink}>{parseLink(secretLink, { useInputCtx: true }).name}</div>
                        ))}
                      </div>
                    </div>
                  }
                />
              ) : isValidating ? (
                <div className="flex items-center gap-1">
                  <Loader className="animate-spin" size={16} />
                  <span className="text-sm font-semibold">Validating</span>
                </div>
              ) : isValid ? (
                <Alert showIcon type={`success`} className="my-2 w-fit" message={`Identity is valid`} />
              ) : (
                <Alert
                  type={`error`}
                  showIcon
                  className="my-2 w-fit"
                  message={`Identity is required`}
                  description={
                    <div>
                      <div className="mb-1">Workload cannot reveal these secrets: </div>
                      <div className="text-sm">
                        {invalidError.split(", ").map((err) => (
                          <div key={err}>{err}</div>
                        ))}
                      </div>
                    </div>
                  }
                />
              )}
            </div>
          ) : null}
          {identityIsRequired && !isValid && !isValidating ? (
            <div className="mt-6">
              <Divider orientation={`left`} orientationMargin={0}>
                Automatically Generate and Assign Identity
              </Divider>
              <CpDescription>
                This creates a new policy and a new identity, that targets the currently selected secrets in this
                workload, so you don't have to create them and configure them manually.
              </CpDescription>
              <CpFormItem>
                <CpLabel name={`identity.identityName`}>Identity Name</CpLabel>
                <CpInput name={`identity.identityName`} style={{ width: 450 }} />
              </CpFormItem>
              <CpFormItem>
                <CpLabel name={`identity.policyName`}>Policy Name</CpLabel>
                <CpInput name={`identity.policyName`} style={{ width: 450 }} />
              </CpFormItem>
              <CpFormItem>
                <Button
                  htmlType={`button`}
                  className="w-fit"
                  loading={isFixing}
                  disabled={isFixing || !identityName || !policyName}
                  onClick={handleFix}
                >
                  Fix Workload Identity
                </Button>
              </CpFormItem>
              {fixError ? (
                <Alert
                  showIcon
                  className={`small w-fit my-2`}
                  type={`error`}
                  message={`Failed to Fix Workload Identity`}
                  description={fixError}
                />
              ) : null}
            </div>
          ) : null}
        </CardContent>
      </Card>
    </div>
  );
};
